import { StandardEvents } from '@shopify/events';

const TIER_30_CENTS = 3000;
const TIER_50_CENTS = 5000;

/**
 * Opens an upsell drawer immediately after an item is added to cart,
 * offering complementary products and showing progress toward the
 * spend-based discount tiers, before handing off to the real cart drawer.
 */
class UpsellDrawerComponent extends HTMLElement {
  connectedCallback() {
    this._onCartUpdate = this._onCartUpdate.bind(this);
    document.addEventListener(StandardEvents.cartLinesUpdate, this._onCartUpdate);

    // Shared backdrop: dims the page behind ANY open theme-drawer (cart or
    // upsell), not just this one, since the native drawer only "squeezes"
    // content on wide viewports and leaves the page behind it interactive.
    const backdrop = document.querySelector('[data-upsell-backdrop]');
    if (backdrop && !backdrop.dataset.wired) {
      backdrop.dataset.wired = 'true';
      const syncBackdrop = () => {
        const anyOpen = document.querySelector('theme-drawer[open]');
        backdrop.classList.toggle('is-visible', Boolean(anyOpen));
      };
      document.addEventListener('theme-drawer:open', syncBackdrop);
      document.addEventListener('theme-drawer:close', syncBackdrop);
      backdrop.addEventListener('click', () => {
        document.querySelectorAll('theme-drawer[open]').forEach((drawer) => drawer.close());
      });
    }

    this.querySelector('[data-upsell-close]')?.addEventListener('click', () => this._dismiss());
    this.querySelector('[data-upsell-no-thanks]')?.addEventListener('click', () => this._dismiss());

    this.querySelectorAll('[data-variant-id]').forEach((btn) => {
      btn.addEventListener('click', () => this._addToCart(btn));
    });

    this._refreshProgress();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this._onCartUpdate);
  }

  get #themeDrawer() {
    return this.closest('theme-drawer');
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  _onCartUpdate(event) {
    if (event.action !== 'add') return;
    // Ignore adds that originated from inside this drawer's own grid.
    if (event.target instanceof Element && this.contains(event.target)) return;
    if (this.#themeDrawer?.isOpen) return;

    // If the add came from inside an open modal dialog (e.g. a quick-add
    // popup), wait for that dialog to close first so focus restoration
    // runs before we open on top of it.
    const sourceModal =
      event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    const openDrawer = () => {
      if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
    };

    event.promise
      ?.then((result) => {
        if (result?.detail?.didError) return;
        if (result?.cart) {
          this._updateProgress(result.cart);
        } else {
          this._refreshProgress();
        }

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openDrawer, { once: true });
        } else {
          openDrawer();
        }
      })
      .catch(() => {});
  }

  _dismiss() {
    this.#themeDrawer?.close();
    const cartDrawer = document.querySelector('#cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  }

  _refreshProgress() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((cart) => this._updateProgress(cart))
      .catch(() => {});
  }

  /**
   * @param {{ total_price: number }} cart
   */
  _updateProgress(cart) {
    const total = Number(cart?.total_price) || 0;
    const fill = this.querySelector('[data-progress-fill]');
    if (fill) {
      const pct = Math.max(0, Math.min(total / TIER_50_CENTS, 1)) * 100;
      fill.style.width = pct + '%';
    }

    const dot30 = this.querySelector('[data-progress-dot="30"]');
    if (dot30) dot30.classList.toggle('is-reached', total >= TIER_30_CENTS);

    const dot50 = this.querySelector('[data-progress-dot="50"]');
    if (dot50) dot50.classList.toggle('is-reached', total >= TIER_50_CENTS);
  }

  _addToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;

    btn.disabled = true;
    const originalLabel = btn.textContent;
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(() => {
        btn.textContent = 'Added';
        btn.classList.add('is-added');
        this._refreshProgress();
        setTimeout(() => {
          btn.textContent = originalLabel;
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 2000);
      })
      .catch(() => {
        btn.textContent = originalLabel;
        btn.disabled = false;
      });
  }
}

if (!customElements.get('upsell-drawer-component')) {
  customElements.define('upsell-drawer-component', UpsellDrawerComponent);
}
